import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { Layout, Menu, Button, Drawer, Space, Typography } from "antd";
import {
  DashboardOutlined,
  AppstoreOutlined,
  SettingOutlined,
  TeamOutlined,            // ← new import
  BookOutlined,
  PictureOutlined,
  CodeOutlined,
  TableOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { PERMISSIONS } from "./permissions";

const { Header, Sider } = Layout;
const { Title } = Typography;

interface NavbarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  perm: { READ: number; WRITE: number };
}

const NavBar: React.FC<NavbarProps> = ({ isOpen, toggleSidebar }) => {
  const { logout, canRead } = useAuth();
  const location = useLocation();
  const [useDrawer, setUseDrawer] = useState(false);
  
  useEffect(() => {
    const handleResize = () => setUseDrawer(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems: NavItem[] = [
    {
      key: "system-management",
      icon: <SettingOutlined style={{ fontSize: 24 }} />,
      label: "System Management",
      path: "/system-management",
      perm: PERMISSIONS.SYSTEM_MANAGEMENT,
    },
    {
      key: "dataset-management",
      icon: <AppstoreOutlined style={{ fontSize: 24 }} />,
      label: "Dataset Management",
      path: "/dataset-management",
      perm: PERMISSIONS.DATASET_MANAGEMENT,
    },
    {
      key: "model-management",
      icon: <CodeOutlined style={{ fontSize: 24 }} />,
      label: "Model Management",
      path: "/model-management",
      perm: PERMISSIONS.MODEL_MANAGEMENT,
    },
    {
      key: "teachmode",
      icon: <BookOutlined style={{ fontSize: 24 }} />,
      label: "Teachmode",
      path: "/teachmode",
      perm: PERMISSIONS.TEACHMODE,
    },
    {
      key: "image-view",
      icon: <PictureOutlined style={{ fontSize: 24 }} />,
      label: "Image View",
      path: "/image-view",
      perm: PERMISSIONS.IMAGE_VIEW,
    },
    {
      key: "dashboard",
      icon: <DashboardOutlined style={{ fontSize: 24 }} />,
      label: "Dashboard",
      path: "/dashboard",
      perm: PERMISSIONS.DASHBOARD,
    },
    {
      key: "role-management",                // ← new entry
      icon: <TeamOutlined style={{ fontSize: 24 }} />,
      label: "Role Management",
      path: "/role-management",
      perm: PERMISSIONS.ROLE_MANAGEMENT,    // ← only shows if canRead this bit
    },
    {
      key: "config-management",                // ← new entry
      icon: <SettingOutlined style={{ fontSize: 24 }} />,
      label: "Config Management",
      path: "/config-management",
      perm: PERMISSIONS.ROLE_MANAGEMENT,    // ← only shows if canRead this bit
    },
    {
      key: "matrix-mapping",
      icon: <TableOutlined style={{ fontSize: 24 }} />,
      label: "Matrix Mapping",
      path: "/matrix-mapping",
      perm: PERMISSIONS.MATRIX_MAPPING,
    },
  ];

  // only keep those the user can READ
  const authorized = navItems.filter(i => canRead(i.perm));

  const selectedKey = () => {
    const found = navItems.find(i => i.path === location.pathname);
    return found ? [found.key] : ['dashboard'];
  };

  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={selectedKey()}
      items={authorized.map(item => ({
        key: item.key,
        icon: item.icon,
        label: <NavLink to={item.path} onClick={toggleSidebar}>{item.label}</NavLink>
      }))}
    />
  );

  const renderMobileDrawer = () => (
    <Drawer
      placement="left"
      closable={false}
      onClose={toggleSidebar}
      open={isOpen}
      width={250}
      bodyStyle={{ padding: 0, backgroundColor: '#001529' }}
    >
      <div className="p-4 flex items-center justify-center bg-gray-800">
        <NavLink to="/" onClick={toggleSidebar}>
          <img className="h-8" src="/logo.png" alt="USS Vision Inc." />
        </NavLink>
      </div>
      {menu}
    </Drawer>
  );

  const renderDesktopSidebar = () => (
    <Sider
      trigger={null}
      collapsible
      collapsed={!isOpen}
      collapsedWidth={0}
      width={250}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 64,
        bottom: 0,
        zIndex: 50
      }}
    >
      {menu}
    </Sider>
  );

  const renderOverlay = () => (
    isOpen && !useDrawer && (
      <div
        className="fixed inset-0 z-40 bg-black opacity-30"
        onClick={toggleSidebar}
      />
    )
  );

  return (
    <>
      <Header style={{
        padding: '0 16px',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0,21,41,.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
        position: 'fixed',
        width: '100%',
        zIndex: 10,
      }}>
        <Space>
          <Button
            type="text"
            icon={isOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={toggleSidebar}
            style={{ fontSize: 16 }}
          />
          <NavLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo.png" alt="USS Vision Inc." style={{ height: 50, marginRight: 10 }} />
            <Title level={4} style={{ margin: 0 }}>Client Portal</Title>
          </NavLink>
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<LogoutOutlined />}
            onClick={logout}
            danger
          >
            Logout
          </Button>
        </Space>
      </Header>

      <div style={{ height: '64px' }} />

      {useDrawer ? renderMobileDrawer() : renderDesktopSidebar()}
      {renderOverlay()}
    </>
  );
};

export default NavBar;
